import React, { useState, useEffect } from 'react';
import { User } from '../types';
import NavigationSidebar from '../components/NavigationSidebar';

interface ContactScreenProps {
  currentUser: User | null;
  onBack: () => void;
  onGoToMenu?: () => void;
  onGoToHelp?: () => void;
  onGoToPrivacySecurity?: () => void;
  onGoToSecurityPolicy?: () => void;
  onGoToCommunityGuidelines?: () => void;
  onGoToTermsOfService?: () => void;
  onGoToAbout?: () => void;
}

const ContactScreen: React.FC<ContactScreenProps> = ({ 
  currentUser, 
  onBack,
  onGoToMenu,
  onGoToHelp,
  onGoToPrivacySecurity,
  onGoToSecurityPolicy,
  onGoToCommunityGuidelines,
  onGoToTermsOfService,
  onGoToAbout,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [name, setName] = useState(currentUser?.username || '');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const API_URL = 'https://voicechatapi.kasaneko.com';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !subject || !message) {
      setError('メールアドレス、件名、メッセージは必須です');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name || null,
          email: email,
          subject: subject,
          message: message,
          userId: currentUser?.userId || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // フォームをリセット
        setName(currentUser?.username || '');
        setEmail('');
        setSubject('');
        setMessage('');
      } else {
        setError(data.error || 'お問い合わせの送信に失敗しました');
      }
    } catch (error) {
      console.error('問い合わせ送信エラー:', error);
      setError('お問い合わせの送信に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Arial, sans-serif',
      color: '#333',
      position: 'relative'
    }}>
      {/* サイドバー（スマホのみ） */}
      {isMobile && (
        <NavigationSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(!sidebarOpen)}
          currentPage="help"
          onGoToMenu={onGoToMenu || onBack}
          onGoToHelp={onGoToHelp || onBack}
          onGoToPrivacySecurity={onGoToPrivacySecurity || onBack}
          onGoToSecurityPolicy={onGoToSecurityPolicy || onBack}
          onGoToCommunityGuidelines={onGoToCommunityGuidelines || onBack}
          onGoToTermsOfService={onGoToTermsOfService || onBack}
          onGoToAbout={onGoToAbout || onBack}
        />
      )}

      {/* ヘッダー */}
      <div style={{
        padding: '15px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.9)'
      }}>
        {isMobile ? (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px',
              color: '#333'
            }}
          >
            ☰
          </button>
        ) : (
          <h1 style={{ margin: 0, fontSize: '24px', color: '#333' }}>お問い合わせ</h1>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isMobile && (
            <button
              onClick={onGoToMenu || onBack}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              メニューに戻る
            </button>
          )}
          {!isMobile && (
            <button
              onClick={onBack}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ← 戻る
            </button>
          )}
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}>
        <div style={{
          maxWidth: '600px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '30px',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }}>
          {success ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '20px'
              }}>
                ✅
              </div>
              <h2 style={{
                fontSize: '24px',
                marginBottom: '15px'
              }}>
                お問い合わせを受け付けました
              </h2>
              <p style={{
                fontSize: '16px',
                marginBottom: '30px',
                lineHeight: '1.6'
              }}>
                お問い合わせいただき、ありがとうございます。<br />
                受付完了のメールをお送りしました。<br />
                回答まで数日かかる場合がございます。あらかじめご了承ください。
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setError(null);
                }}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginRight: '10px'
                }}
              >
                もう一度送信
              </button>
              <button
                onClick={onBack}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  padding: '12px 30px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                メニューに戻る
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  お名前（任意）
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#333',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="お名前を入力"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  メールアドレス <span style={{ color: '#ffd700' }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#333',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="your-email@example.com"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  件名 <span style={{ color: '#ffd700' }}>*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#333',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="問い合わせの件名を入力"
                />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  メッセージ <span style={{ color: '#ffd700' }}>*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={8}
                  maxLength={5000}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#333',
                    fontSize: '16px',
                    fontFamily: 'Arial, sans-serif',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  placeholder="お問い合わせ内容を入力してください（最大5000文字）"
                />
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'right',
                  marginTop: '5px'
                }}>
                  {message.length} / 5000 文字
                </div>
              </div>

              {error && (
                <div style={{
                  background: 'rgba(255, 87, 87, 0.1)',
                  border: '1px solid #ff5757',
                  color: '#c62828',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '15px',
                  background: loading ? '#999' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? '送信中...' : '送信する'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* フッター */}
      <div style={{
        padding: '10px 20px',
        textAlign: 'center',
        fontSize: '12px',
        color: '#666',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.9)'
      }}>
        Voitter © 2025
      </div>
    </div>
  );
};

export default ContactScreen;

